import { useIsFetching, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState, type ReactNode } from "react";
import { z } from "zod";

import type { AddTeamMemberRequest, TeamMember } from "@ravnar/api-contract";

import { useAskAgain } from "../../api/refresh";
import { useSession } from "../../api/session";
import { removeTeamMember, resetTeamMemberMfa, saveTeamMember, teamMembersQueryOptions } from "../../api/settings";
import { PageHeader } from "../../components/shell/app-shell";
import { Avatar } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { Card, CardHeader } from "../../components/ui/card";
import { Drawer } from "../../components/ui/drawer";
import { Badge } from "../../components/ui/pill";
import { Table, type TableColumn } from "../../components/ui/table";
import { Updating } from "../../components/ui/updating";
import { assignableRoles, MEMBER_ROLE_LABELS } from "../../domain/settings";
import { initialsOf } from "../../lib/format";
import { showToast } from "../../lib/toast";
import { AccessSummary } from "./-components/access-summary";
import { MemberForm } from "./-components/member-form";

const teamSearchSchema = z.object({
	/** "new" opens the drawer to add a member; a member id opens that member. */
	member: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/settings/team")({
	validateSearch: teamSearchSchema,
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: ({ context }) => context.queryClient.query({ ...teamMembersQueryOptions(), staleTime: "static" }),
	head: () => ({ meta: [{ title: "Team & Access · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Team & Access"] },
	component: TeamPage,
});

function TeamPage() {
	const { member: selectedId } = Route.useSearch();
	const navigate = Route.useNavigate();
	const membersQuery = teamMembersQueryOptions();
	const { data: members } = useSuspenseQuery(membersQuery);
	const isUpdating = useIsFetching({ queryKey: membersQuery.queryKey, exact: true }) > 0;
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const formId = useId();
	const [confirmingRemovalOf, setConfirmingRemovalOf] = useState<string | undefined>();
	const [confirmingMfaResetOf, setConfirmingMfaResetOf] = useState<string | undefined>();
	const { member: you } = useSession();

	// The page offers only what the API allows (assignableRoles): members add nobody, and admins do not change owners.
	const canAdd = assignableRoles(you.role).length > 0;
	const isAdding = selectedId === "new" && canAdd;
	const selected = members.find(entry => entry.id === selectedId);
	const isYou = selected?.id === you.id;
	const canChange = isAdding || (selected !== undefined && assignableRoles(you.role, selected.role).length > 0);

	// Every save refreshes the members and the session in case you renamed yourself; the audit entry arrives through a
	// queue a moment after this answer, so it is asked again as it catches up.
	const refreshSettings = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: ["settings"] }),
			queryClient.invalidateQueries({ queryKey: ["session"] }),
			askAgain(["settings", "audit-log"]),
		]);
	const saveMutation = useMutation({ mutationFn: saveTeamMember, onSuccess: refreshSettings });
	const removeMutation = useMutation({ mutationFn: removeTeamMember, onSuccess: refreshSettings });
	const resetMfaMutation = useMutation({ mutationFn: resetTeamMemberMfa, onSuccess: refreshSettings });

	const closeDrawer = () => {
		setConfirmingRemovalOf(undefined);
		setConfirmingMfaResetOf(undefined);
		saveMutation.reset();
		removeMutation.reset();
		resetMfaMutation.reset();
		void navigate({ search: previous => ({ ...previous, member: undefined }), replace: true });
	};

	const saveMember = async (input: AddTeamMemberRequest) => {
		try {
			await saveMutation.mutateAsync({ id: selected?.id, member: input });
		} catch {
			// The alert in the drawer shows saveMutation.error.
			return;
		}
		closeDrawer();
		showToast(selected === undefined ? `${input.name} added to the team.` : `Changes to ${input.name} saved.`);
	};

	const removeMember = async (member: TeamMember) => {
		try {
			await removeMutation.mutateAsync(member.id);
		} catch {
			// The alert in the drawer shows removeMutation.error.
			return;
		}
		closeDrawer();
		showToast(`${member.name} removed from the team.`);
	};

	const resetMemberMfa = async (member: TeamMember) => {
		try {
			await resetMfaMutation.mutateAsync(member.id);
		} catch {
			// The alert in the drawer shows resetMfaMutation.error.
			return;
		}
		closeDrawer();
		showToast(`${member.name} can set up a new authenticator app.`);
	};

	const columns: readonly TableColumn<TeamMember>[] = [
		{
			key: "member",
			header: "Member",
			cell: member => (
				<span className="flex items-center gap-2.5">
					<Avatar initials={initialsOf(member.name)} size="sm" />
					<span className="min-w-0">
						<Link
							to="/settings/team"
							search={{ member: member.id }}
							className="block font-medium hover:underline"
						>
							{member.name}
						</Link>
						<span className="block text-caption text-ink-3">
							{member.id === you.id ? `${member.email} · you` : member.email}
						</span>
					</span>
				</span>
			),
		},
		{ key: "role", header: "Role", cell: member => <Badge>{MEMBER_ROLE_LABELS[member.role]}</Badge> },
		{ key: "access", header: "Access", cell: member => <AccessSummary member={member} /> },
	];

	const confirmingRemoval = selected !== undefined && confirmingRemovalOf === selected.id;
	const confirmingMfaReset = selected !== undefined && confirmingMfaResetOf === selected.id;
	const error = saveMutation.error ?? removeMutation.error ?? resetMfaMutation.error;

	// A member you may not change opens read-only, without a footer.
	let footer: ReactNode;
	if (confirmingRemoval) {
		footer = (
			<>
				<p className="mr-auto text-caption text-alert">
					Remove {selected.name}? They lose access straight away.
				</p>
				<Button
					onClick={() => {
						setConfirmingRemovalOf(undefined);
					}}
				>
					Keep
				</Button>
				<Button
					className="border-alert-mid bg-alert-soft text-alert hover:bg-alert-mid hover:text-alert"
					onClick={() => {
						void removeMember(selected);
					}}
				>
					Remove {selected.name}
				</Button>
			</>
		);
	} else if (confirmingMfaReset) {
		footer = (
			<>
				<p className="mr-auto text-caption text-ink-2">
					Reset two-step verification for {selected.name}? They sign in with their password alone until they
					set up a new authenticator app.
				</p>
				<Button
					onClick={() => {
						setConfirmingMfaResetOf(undefined);
					}}
				>
					Keep
				</Button>
				<Button
					variant="primary"
					onClick={() => {
						void resetMemberMfa(selected);
					}}
				>
					Reset two-step verification
				</Button>
			</>
		);
	} else if (canChange) {
		footer = (
			<>
				{selected === undefined || isYou ? null : (
					<span className="mr-auto flex gap-2">
						<Button
							className="text-alert hover:text-alert"
							onClick={() => {
								setConfirmingRemovalOf(selected.id);
							}}
						>
							Remove member
						</Button>
						<Button
							onClick={() => {
								setConfirmingMfaResetOf(selected.id);
							}}
						>
							Reset two-step verification
						</Button>
					</span>
				)}
				<Button onClick={closeDrawer}>Cancel</Button>
				<Button type="submit" form={formId} variant="primary">
					{isAdding ? "Add member" : "Save changes"}
				</Button>
			</>
		);
	}

	return (
		<>
			<PageHeader
				title="Team & Access"
				description="Who can sign in, and what they can see in each section. Owners and admins are editors everywhere and manage the team, but only owners make someone an admin or an owner. New members get an email with a temporary password. An owner or admin can reset a member's two-step verification when they lose the authenticator app it was set up on."
				actions={
					canAdd ? (
						<Button
							variant="primary"
							onClick={() => {
								void navigate({ search: previous => ({ ...previous, member: "new" }) });
							}}
						>
							Add member
						</Button>
					) : undefined
				}
			/>
			<Card>
				<CardHeader
					title={`${members.length} ${members.length === 1 ? "member" : "members"}`}
					action={<Updating active={isUpdating} />}
				/>
				<Table
					label="Team members"
					columns={columns}
					rows={members}
					rowKey={member => member.id}
					interactiveRows
				/>
			</Card>
			<Drawer
				open={isAdding || selected !== undefined}
				title={isAdding ? "Add member" : (selected?.name ?? "")}
				onClose={closeDrawer}
				footer={footer}
			>
				{isAdding || selected !== undefined ? (
					<div className="flex flex-col gap-4">
						<MemberForm
							key={selectedId}
							member={selected}
							isYou={isYou}
							yourRole={you.role}
							formId={formId}
							onSave={saveMember}
						/>
						{error === null ? null : (
							<p role="alert" className="text-caption text-alert">
								{error.message}
							</p>
						)}
					</div>
				) : null}
			</Drawer>
		</>
	);
}
